import React, { useState } from "react";
import {
  FaHome, FaTachometerAlt, FaUserFriends, FaWallet, FaUserCircle,
  FaExchangeAlt, FaTools, FaFileAlt, FaCogs
} from "react-icons/fa";
import { Link, useLocation } from "react-router-dom";
import logo from "../Assets/images/logo.jpg";
import { useAuth } from '../Context/AuthContext';
import LogoutButton from './LogoutButton';
import LogoutErrorAlert from './LogoutErrorAlert';
import { formatUserName } from '../Utils/userUtils';
import { getMaintenanceStatus, setMaintenanceMode } from '../API/ProtectedAuth';
import ConfirmLogoutModal from './ConfirmLogoutModal';
import { useEffect } from 'react';

const menu = [
  { label: "الصفحة الرئيسية", icon: <FaHome />, link: "/home" },
  { label: "لوحة التحكم", icon: <FaTachometerAlt />, link: "/dashboard" },
  { label: "قائمة التجار", icon: <FaUserFriends />, link: "/merchants" },
  { label: "عمليات المحفظة", icon: <FaWallet />, link: "/wallet-transactions" },
  { label: "عمليات التحويل", icon: <FaExchangeAlt />, link: "/services-transactions" },
];

const bottomMenu = [
  { label: "قائمة الخدمات", icon: <FaTools />, link: "/mtn-syriatel-operations" },
  { label: "إدارة الخدمات", icon: <FaTools />, link: "/service-management" },
  { label: "عمليات التحويل لكافة المستخدمين", icon: <FaExchangeAlt />, link: "/admin-transactions" },
  { label: "محافظ المستخدمين", icon: <FaWallet />, link: "/admin-wallets" },
  { label: "التقارير", icon: <FaFileAlt />, link: "/reports" },
];

const Sidebar = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [maintenance, setMaintenance] = useState(false);
  const [maintenanceLoading, setMaintenanceLoading] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingMaintenanceState, setPendingMaintenanceState] = useState(null);
  const [showMaintenanceSwitch, setShowMaintenanceSwitch] = useState(false);

  // Fetch maintenance status on component mount
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const data = await getMaintenanceStatus();
        if (data && typeof data.is_maintenance !== 'undefined') {
          setMaintenance(data.is_maintenance);
        }
      } catch (error) {
        console.error("Failed to fetch maintenance status", error);
      }
    };
    fetchStatus();
  }, []);

  // Show confirmation dialog before toggling
  const handleToggleClick = () => {
    setPendingMaintenanceState(!maintenance);
    setShowConfirmDialog(true);
  };

  // Handle confirmed maintenance toggle
  const handleConfirmToggle = async () => {
    setMaintenanceLoading(true);
    setShowConfirmDialog(false);
    try {
      await setMaintenanceMode(pendingMaintenanceState);
      setMaintenance(pendingMaintenanceState);
    } catch (error) {
      console.error("Failed to update maintenance mode", error);
      alert("فشل تحديث وضع الصيانة");
    } finally {
      setMaintenanceLoading(false);
      setPendingMaintenanceState(null);
    }
  };

  // Handle cancel confirmation
  const handleCancelToggle = () => {
    setShowConfirmDialog(false);
    setPendingMaintenanceState(null);
  };

  const isActive = (path) => location.pathname === path;
  const toggleCollapse = () => setCollapsed(!collapsed);

  return (
    <aside
      className={`${collapsed ? 'w-20' : 'w-72'} h-screen flex flex-col justify-between bg-gradient-to-b from-main1 to-[#153426] border-l border-white/5 font-cairo transition-all duration-300 overflow-hidden z-50 shadow-2xl`}
      aria-label="Sidebar navigation"
    >
      {/* Header Section */}
      <div className="flex flex-col flex-1 min-h-0">
        <div
          className="flex items-center gap-3 px-5 py-6 border-b border-white/5 relative cursor-pointer hover:bg-white/5 transition-colors duration-200"
          onClick={toggleCollapse}
        >
          <img
            src={logo}
            alt="شعار الموقع"
            className={`${collapsed ? 'w-10 h-10 mx-auto' : 'w-11 h-11'} rounded-xl shadow-lg shadow-black/20 ring-1 ring-white/10 transition-all duration-300 shrink-0`}
          />
          <div className={`flex flex-col whitespace-nowrap transition-all duration-300 ${collapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100'}`}>
            <span className="text-lg font-bold text-main2 tracking-wide">نمبر ون</span>
            <span className="text-[10px] text-gray-400">لوحة التحكم</span>
          </div>

          {/* Visibility Toggle Button */}
          {!collapsed && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowMaintenanceSwitch(!showMaintenanceSwitch);
              }}
              className={`absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-lg transition-all duration-300 ${showMaintenanceSwitch ? 'bg-main2 text-white shadow-lg' : 'text-gray-400 hover:bg-white/10'}`}
              title={showMaintenanceSwitch ? "إخفاء مفتاح الصيانة" : "اظهار مفتاح الصيانة"}
            >
              <FaCogs size={16} />
            </button>
          )}
        </div>

        <div className={`px-4 transition-all duration-300 ${collapsed ? 'px-2' : ''}`}>
          <LogoutErrorAlert />
        </div>

        {/* Maintenance Toggle */}
        <div className={`transition-all duration-500 ease-in-out overflow-hidden flex-shrink-0 ${showMaintenanceSwitch && !collapsed ? 'max-h-24 opacity-100 mt-4 mb-2' : 'max-h-0 opacity-0 mb-0'}`}>
          <div className={`mx-4 relative flex items-center bg-white/5 rounded-2xl p-1 border border-white/10 transition-all duration-500 ${maintenance
            ? 'border-[#C03F2A]/30 shadow-[#C03F2A]/10'
            : 'border-main2/30 shadow-main2/10'
            }`}>
            <button
              onClick={() => !maintenanceLoading && !maintenance && handleToggleClick()}
              disabled={maintenanceLoading}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-[12px] font-bold transition-all duration-300 relative z-10 ${maintenance
                ? "text-white"
                : "text-gray-400 hover:text-gray-200"
                }`}
            >
              {maintenanceLoading && !maintenance ? (
                <span className="animate-spin w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full"></span>
              ) : (
                <FaTools size={12} className={maintenance ? "text-white" : "text-gray-400"} />
              )}
              <span>صيانة</span>
            </button>

            <button
              onClick={() => !maintenanceLoading && maintenance && handleToggleClick()}
              disabled={maintenanceLoading}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-[12px] font-bold transition-all duration-300 relative z-10 ${!maintenance
                ? "text-white"
                : "text-gray-400 hover:text-gray-200"
                }`}
            >
              {maintenanceLoading && maintenance ? (
                <span className="animate-spin w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full"></span>
              ) : (
                <FaTachometerAlt size={12} className={!maintenance ? "text-white" : "text-gray-400"} />
              )}
              <span>تشغيل</span>
            </button>

            {/* Sliding Background */}
            <div className={`absolute inset-y-1 w-[calc(50%-4px)] rounded-xl shadow-lg pointer-events-none transition-all duration-500 ease-out ${maintenance ? "right-1 bg-[#C03F2A]" : "left-1 bg-main2"
              }`} />
          </div>
        </div>

        {/* Navigation Links Area */}
        <nav
          className="flex-1 flex flex-col min-h-0 overflow-hidden"
          aria-label="Main navigation"
        >
          {/* Scrollable links */}
          <div className={`flex-1 overflow-y-auto scrollbar-hide py-2 ${collapsed ? 'px-2' : 'px-3'} mt-1`}>
            {menu.map((item) => (
              <Link
                key={item.label}
                to={item.link}
                className={`relative flex items-center ${collapsed ? 'justify-center px-2' : 'gap-3 px-4'} py-3 rounded-xl transition-all duration-300 group/link overflow-hidden ${isActive(item.link)
                  ? "bg-main2/10 text-main2 shadow-inner shadow-main2/5"
                  : "text-gray-400 hover:bg-white/5 hover:text-gray-200"
                  }`}
                title={collapsed ? item.label : undefined}
                aria-label={item.label}
              >
                <span className={`text-lg relative z-10 transition-transform duration-300 ${isActive(item.link) ? "" : "group-hover/link:scale-110"}`}>
                  {item.icon}
                </span>
                <span className={`font-medium text-sm whitespace-nowrap relative z-10 transition-all duration-300 ${collapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100'
                  }`}>
                  {item.label}
                </span>
                {isActive(item.link) && !collapsed && (
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 h-8 w-1 bg-main2 rounded-l-full shadow-[0_0_12px_rgba(199,136,43,0.6)]" />
                )}
                {isActive(item.link) && collapsed && (
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-main2 rounded-r-full shadow-[0_0_12px_rgba(199,136,43,0.6)]" />
                )}
              </Link>
            ))}

            <div className={`my-3 border-t border-white/5 transition-all duration-300 ${collapsed ? 'mx-2' : 'mx-6'}`} />

            {bottomMenu.map((item) => (
              <Link
                key={item.label}
                to={item.link}
                className={`relative flex items-center ${collapsed ? 'justify-center px-2' : 'gap-3 px-4'} py-3 rounded-xl transition-all duration-300 group/link overflow-hidden ${isActive(item.link)
                  ? "bg-main2/10 text-main2 shadow-inner shadow-main2/5"
                  : "text-gray-400 hover:bg-white/5 hover:text-gray-200"
                  }`}
                title={collapsed ? item.label : undefined}
                aria-label={item.label}
              >
                <span className={`text-lg relative z-10 transition-transform duration-300 ${isActive(item.link) ? "" : "group-hover/link:scale-110"}`}>
                  {item.icon}
                </span>
                <span className={`font-medium text-sm whitespace-nowrap relative z-10 transition-all duration-300 ${collapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100'
                  }`}>
                  {item.label}
                </span>
                {isActive(item.link) && !collapsed && (
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 h-8 w-1 bg-main2 rounded-l-full shadow-[0_0_12px_rgba(199,136,43,0.6)]" />
                )}
                {isActive(item.link) && collapsed && (
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-main2 rounded-r-full shadow-[0_0_12px_rgba(199,136,43,0.6)]" />
                )}
              </Link>
            ))}
          </div>

          {/* Sticky Logout Button container */}
          <div className={`mt-auto pt-4 pb-2 transition-all duration-300 ${collapsed ? 'px-2' : 'px-4'} flex-shrink-0`}>
            <LogoutButton
              collapsed={collapsed}
              className="bg-red-500/5 border border-white/5 hover:bg-red-500/20 hover:border-red-500/30 text-red-200/80 hover:text-white rounded-2xl py-3 shadow-lg backdrop-blur-sm"
            />
          </div>
        </nav>
      </div>

      {/* Confirmation Dialog */}
      <ConfirmLogoutModal
        isOpen={showConfirmDialog}
        onClose={handleCancelToggle}
        onConfirm={handleConfirmToggle}
        isLoading={maintenanceLoading}
        title={pendingMaintenanceState ? "تأكيد تفعيل وضع الصيانة" : "تأكيد إلغاء وضع الصيانة"}
        message={
          pendingMaintenanceState
            ? "هل أنت متأكد من تفعيل وضع الصيانة؟"
            : "هل أنت متأكد من إلغاء وضع الصيانة وتشغيل النظام؟"
        }
        subMessage={
          pendingMaintenanceState
            ? "سيتم منع المستخدمين من الوصول إلى النظام مؤقتاً"
            : "سيتم السماح للمستخدمين بالوصول إلى النظام"
        }
        confirmLabel={pendingMaintenanceState ? "تفعيل الصيانة" : "تشغيل النظام"}
        cancelLabel="إلغاء"
        variant={pendingMaintenanceState ? "warning" : "success"}
        icon={pendingMaintenanceState ? FaTools : FaTachometerAlt}
      />

      {/* User Profile */}
      <Link
        to="/profile"
        className={`${collapsed ? 'mx-2' : 'mx-4'} mb-6 p-2 flex items-center ${collapsed ? 'justify-center' : 'gap-3'} bg-white/5 rounded-2xl border border-white/5 backdrop-blur-md hover:bg-white/10 transition-all duration-300 group/profile overflow-hidden cursor-pointer shadow-xl hover:shadow-2xl hover:border-white/10`}
        title={collapsed ? formatUserName(user) : undefined}
        aria-label="User profile"
      >
        <div className="relative shrink-0">
          <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-main2 to-amber-700 text-white shadow-lg shadow-black/30 group-hover/profile:scale-105 transition-transform duration-300">
            <FaUserCircle size={22} />
          </span>
          <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-green-500 border-[3px] border-[#1C4130] rounded-full"></span>
        </div>

        <div className={`flex-1 min-w-0 transition-all duration-300 ${collapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100'
          }`}>
          <div className="font-bold text-gray-100 truncate text-sm">
            {formatUserName(user)}
          </div>
          <div className="text-[10px] text-main2/80 truncate mt-0.5 font-medium">
            عرض الملف الشخصي
          </div>
        </div>
      </Link>
    </aside>
  );
};

export default Sidebar;
